import { KTag, KText } from '@/ui-library/components';
import { Flex, Icon, useDisclosure } from '@chakra-ui/react';
import { AlertTriangle, InfoCircle } from '@kula-ai/icons-core';
import { type ComponentType } from 'react';
import { KPopover } from '@/ui-library/components/organisms/KPopover/KPopover';

interface TokenInfoConfig {
  id: string;
  label: string;
  tokens: string[];
  show: boolean;
  showIcon?: boolean;
  icon?: ComponentType<any>;
  iconProps?: Record<string, any>;
}

interface TokenValidationProps {
  validTokens?: string[];
  invalidTokens?: string[];
  nonFilledValidTokens?: string[];
  filledTokensNotInTemplate?: string[];
}

export const TokenValidation = ({
  validTokens,
  invalidTokens,
  nonFilledValidTokens,
  filledTokensNotInTemplate,
}: TokenValidationProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const tokensInfoConfig: TokenInfoConfig[] = [
    {
      id: 'valid',
      label: 'Tokens in the template',
      tokens: validTokens,
      show:
        invalidTokens?.length === 0 &&
        nonFilledValidTokens?.length === 0 &&
        filledTokensNotInTemplate?.length === 0,
    },
    {
      id: 'invalid',
      label: 'Invalid tokens',
      tokens: invalidTokens,
      showIcon: true,
      show: invalidTokens?.length > 0,
      icon: AlertTriangle,
      iconProps: { color: 'error-200' },
    },
    {
      id: 'nonFilled',
      label: 'Fields not filled for tokens present in the template',
      tokens: nonFilledValidTokens,
      showIcon: true,
      show: nonFilledValidTokens?.length > 0,
      icon: AlertTriangle,
      iconProps: { color: 'error-200' },
    },
  ];

  switch (true) {
    case filledTokensNotInTemplate?.length > 0:
      return (
        <KPopover
          triggerElement={
            <Flex
              alignItems="center"
              gap="6px"
              bg="secondary-50"
              p="2px 8px"
              borderRadius="xs"
              cursor="pointer"
              w="fit-content"
            >
              <InfoCircle size="1em" className="kula-icon" />
              <KText color="secondary-900" size="sm-medium">
                Info
              </KText>
              <KTag
                value={filledTokensNotInTemplate?.length}
                bg="white"
                borderRadius="4px"
                borderWidth="1px"
                borderColor="border-color"
                px="8px"
                py="2px"
              />
            </Flex>
          }
          popoverActions={{ isOpen, onOpen, onClose }}
          popoverProps={{
            placement: 'bottom-start',
            trigger: 'hover',
          }}
          popoverContentProps={{
            minW: '400px',
            maxW: '800px',
            maxH: '300px',
            overflowY: 'auto',
            bg: 'white',
            borderColor: 'border-color',
            borderWidth: '1px',
            borderRadius: '6px',
          }}
          showArrow
          showCloseButton={false}
        >
          <Flex direction="column" gap="4px" p="4px">
            <KText fontWeight="medium" mb="4px" color="secondary-900">
              Offer fields filled but not added to the template
            </KText>
            {filledTokensNotInTemplate?.map((token) => (
              <KTag
                key={token}
                value={`{{${token}}}`}
                w="fit-content"
                display="inline-block"
              />
            ))}
          </Flex>
        </KPopover>
      );

    default:
      return (
        <>
          {tokensInfoConfig?.map((item) => {
            return (
              item?.show && (
                <Flex key={item?.id} mt="10px" gap="10px" flexDir="column">
                  <Flex
                    alignItems="center"
                    gap="6px"
                    p="16px 24px"
                    bg="secondary-25"
                    borderTopWidth="1px"
                    borderBottomWidth="1px"
                    borderColor="border-color"
                  >
                    {item?.icon && (
                      <Icon as={item?.icon} {...item?.iconProps} />
                    )}
                    <KText color="secondary-900">{item?.label}</KText>
                  </Flex>
                  <Flex gap="10px" flexWrap="wrap" px="10px">
                    {item?.tokens.map((token, index) => (
                      <KTag key={`${token}-${index}`} value={`{{${token}}}`} />
                    ))}
                  </Flex>
                </Flex>
              )
            );
          })}
        </>
      );
  }
};
